import type { useQuery } from "@apollo/client/react";
import { reverse } from "lodash-es";
import type {
  CheckedStateRecord,
  FilterItemProps,
} from "@jana-technology/platform-ui";

import type {
  AssetSelectionQuery,
  AssetSelectionQueryVariables,
} from "$/__generated__/graphql";

import type { FilteredWellNamesByHierarchy } from "../types";

export const flattenHierarchyIds = (
  items: NonNullable<
    NonNullable<
      useQuery.Result<AssetSelectionQuery, AssetSelectionQueryVariables>["data"]
    >["hierarchies"]
  >,
): Array<number> => {
  const stack = reverse([...items]);
  const result = [];

  let curr;
  while (stack.length) {
    curr = stack.pop();
    // check for undefined and null hierarchy ids
    if (typeof curr?.hierarchyId === "number") {
      result.push(curr.hierarchyId);
    }
    // recurse over children
    if (curr?.items?.length) {
      for (let i = curr.items.length - 1; i >= 0; i -= 1) {
        stack.push(curr.items[i]);
      }
    }
  }
  return result;
};

export const makeFilterItemProps = (
  items: NonNullable<
    NonNullable<
      useQuery.Result<AssetSelectionQuery, AssetSelectionQueryVariables>["data"]
    >["hierarchies"]
  >,
  filteredWellNames: FilteredWellNamesByHierarchy,
): Array<FilterItemProps> =>
  items.map((hierarchy, ind) => {
    const stringLabel = hierarchy?.name ?? `Unnamed Hierarchy ${ind + 1}`;
    const value =
      typeof hierarchy?.hierarchyId === "number"
        ? String(hierarchy.hierarchyId)
        : stringLabel;

    if (hierarchy?.items?.length) {
      return {
        label: stringLabel,
        value,
        children: makeFilterItemProps(hierarchy.items, filteredWellNames),
      };
    }

    // we may not find any matches if no wells or if value is not a number (e.g. unnamed hierarchy), so replace w/ 0
    const matchingWells = filteredWellNames?.find(
      (hierarchy) => hierarchy.hierarchyId === Number(value),
    );
    const numMatchingWells = matchingWells?.wellNames.length ?? 0;
    return {
      label: stringLabel,
      value,
      badge: numMatchingWells.toString(),
    };
  });

export const convertCheckboxStatesToHierarchyIds = (
  checkboxStates: CheckedStateRecord,
) =>
  Object.keys(checkboxStates)
    .filter((key) => {
      return checkboxStates[key] === true;
    })
    .map(Number)
    .filter(removeNodes);

const removeNodes = (num: number) => !Number.isNaN(num);
